import { v } from '../math/vec';
import { wrapAngle } from '../math/geom';
import type { CarState, InputFrame } from './types';
import type { CarTuning, SurfaceMod } from './tuning';

// Advances a car's simulation state by one tick using an arcade-style model:
// speed-scaled steering (turning is slower at low speed, near-instant at
// standstill) and speed-and-steer-scaled grip loss (hard steering above the
// drift threshold at speed lets the car slide instead of gripping fully).
//
// The heading is rotated BEFORE velocity is touched, then the still-unchanged
// world velocity is decomposed into forward/lateral components using the NEW
// heading. A vector that pointed straight along the old heading now has a
// lateral component in the new frame, proportional to how far the heading
// just turned: that carried-over sideways momentum is the drift, and grip
// below decides how quickly it bleeds off. Decomposing in the old frame and
// recomposing in the new one (the naive order) would instead be a pure
// rotation of the velocity vector and could never produce drift.
export function stepCar(
  car: CarState,
  input: InputFrame,
  tuning: CarTuning,
  surface: SurfaceMod,
  dt: number,
): void {
  if (car.airborne > 0) {
    car.airborne = Math.max(0, car.airborne - dt);
    car.pos = v.add(car.pos, v.scale(car.vel, dt));
    return; // no steering, no grip, no drag in the air
  }

  // Orientation and overall speed from the heading before this tick's
  // rotation. dir treats a car with no forward component (including one at
  // rest) as forward-oriented, so standstill steering degrades gracefully
  // via speedFrac being 0 rather than via an ambiguous sign.
  const fwdOld = v.fromAngle(car.heading);
  const dir = v.dot(car.vel, fwdOld) >= 0 ? 1 : -1;
  const speed = v.len(car.vel);

  // Steering: turn rate scales with overall speed, full rate at or above
  // turnFullSpeed.
  const speedFrac = Math.min(1, speed / tuning.turnFullSpeed);
  car.heading = wrapAngle(car.heading + input.steer * tuning.turnRate * speedFrac * dir * dt);

  // Decompose the still-unchanged world velocity in the NEW heading frame.
  const fwd = v.fromAngle(car.heading);
  const right = { x: -fwd.y, y: fwd.x };
  let vf = v.dot(car.vel, fwd);
  let vl = v.dot(car.vel, right);
  const maxSpeed = tuning.maxSpeed * surface.speed;

  // Longitudinal: throttle accelerates forward, brake slows or reverses.
  if (input.throttle > 0) vf += tuning.accel * input.throttle * dt;
  if (input.brake > 0) {
    if (vf > 0.2) vf -= tuning.brake * input.brake * dt;
    else vf -= tuning.reverseAccel * input.brake * dt;
  }
  vf -= vf * tuning.drag * dt;
  vf = Math.max(-tuning.maxReverse, Math.min(maxSpeed, vf));

  // Grip: hard steering above the drift threshold at speed lets lateral
  // velocity decay more slowly, i.e. the car slides instead of gripping.
  const driftAmount =
    (Math.max(0, Math.abs(vf) / tuning.maxSpeed - tuning.driftThreshold) / (1 - tuning.driftThreshold)) *
    Math.abs(input.steer);
  const keep = Math.max(
    0,
    Math.min(0.999, (tuning.gripHigh + (tuning.gripLow - tuning.gripHigh) * Math.min(1, driftAmount)) * surface.grip),
  );
  vl *= Math.pow(keep, dt * 60); // per-tick factor expressed for arbitrary dt

  car.vel = v.add(v.scale(fwd, vf), v.scale(right, vl));
  car.pos = v.add(car.pos, v.scale(car.vel, dt));
}
